import { useReportsAmounts } from "@circle-react/components/SettingsApp/Moderation/useReportsAmounts";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  isCommunityAdmin,
  isCommunityModerator,
} from "@circle-react/helpers/communityMemberHelpers";
import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { liveStreamsRoutes } from "@circle-react/lib/routes/liveStreams";

import { t } from "@/i18n-js/instance";

import { NavGroup } from "../NavGroup";
import { SubNavLinkItem } from "./SubNavLinkItem";
import type { Link } from "./types";

const useContentLinksBase = (moderationBadgeLabel: string): Link[] => {
  const { currentCommunityMember } = usePunditUserContext();

  const links: Link[] = [
    {
      label: t("settings_nav_v3.content.media_manager"),
      path: settingsNavPathV3.content.files,
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      isNewFeature: true,
      exact: false,
    },
    {
      label: t("settings_nav_v3.content.posts"),
      path: settingsNavPathV3.content.posts,
      isDisabled: !(
        isCommunityAdmin(currentCommunityMember) ||
        isCommunityModerator(currentCommunityMember)
      ),
      exact: true,
    },
    {
      label: t("settings_nav_v3.site.pages"),
      path: settingsNavPathV3.content.pages,
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      exact: true,
    },
    {
      label: t("settings_nav_v3.site.spaces"),
      path: settingsNavPathV3.content.spaces,
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      exact: false,
    },
    {
      label: t("settings_nav_v3.content.topics"),
      path: settingsNavPathV3.content.topics,
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      exact: false,
    },
    {
      label: t("settings_nav_v3.content.moderation"),
      path: settingsNavPathV3.content.moderation,
      isDisabled: !(
        isCommunityAdmin(currentCommunityMember) ||
        isCommunityModerator(currentCommunityMember)
      ),
      badgeLabel: moderationBadgeLabel,
      exact: false,
    },
    {
      label: t("settings_nav_v3.live_streams.label"),
      path: liveStreamsRoutes.index(),
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      exact: false,
    },
    {
      label: t("settings_nav_v3.content.bulk_logs"),
      path: settingsNavPathV3.content.posts_bulk_actions,
      isDisabled: !isCommunityAdmin(currentCommunityMember),
      exact: true,
    },
  ];

  return links;
};

const useContentLinks = (): Link[] => {
  const { inboxCount: moderationInboxCount } = useReportsAmounts();

  return useContentLinksBase(
    moderationInboxCount > 0 ? String(moderationInboxCount) : "",
  );
};

export const useContentLinksWithoutModerationBadge = (): Link[] =>
  useContentLinksBase("");

export const ContentLinks = () => {
  const links = useContentLinks();

  return (
    <NavGroup groupLinks={links}>
      <NavGroup.Content title={t("settings_nav_v3.content.label")}>
        {links.map(link => (
          <SubNavLinkItem key={link.path} to={link.path} {...link} />
        ))}
      </NavGroup.Content>
    </NavGroup>
  );
};
